// Api
import Api, { apiStart, apiEnd, apiError, requestSuccess, requestFail } from "@Api";
import { get, isEmpty } from "lodash";

// Socket
import { socketClose, socketInit } from "@Socket";

// Redux
import { LOGIN, LOGOUT, REHYDRATE, CHOOSE_COMPANY, CHANGE_STATE } from "@Actions/types";
import { displayNotification } from "@Actions/notifications";

export function logout({
	withoutServer = false,
	auto = false,
	withMessage = false,
	message = "Fuiste desconectado porque alguien más está usando tu cuenta",
} = {}) {
	return async (dispatch) => {
		const handleLogout = async () => {
			sessionStorage.removeItem("store");
			dispatch(requestSuccess(LOGOUT));
			Api.defaults.headers.common.Authorization = `Bearer `;
			socketClose();
			dispatch(apiEnd());
		};
		dispatch(apiStart());
		if (withoutServer) {
			await handleLogout();
			dispatch(apiError(message));
		} else {
			Api.post("/authentication/logout", {
				auto,
			})
				.then(async () => {
					await handleLogout();
					if (withMessage) {
						dispatch(apiError(message));
					}
				})
				.catch((error) => {
					dispatch(apiEnd());
					dispatch(apiError(error.message));
				});
		}
	};
}

export function rehydrate() {
	return (dispatch) => {
		const store = sessionStorage.getItem("store");
		if (store) {
			const parsedStore = JSON.parse(store);
			Api.defaults.headers.common.Authorization = `Bearer ${parsedStore.user.token}`;
			dispatch(requestSuccess(REHYDRATE, parsedStore));
			if (parsedStore.company.companyId) {
				socketInit({
					userId: parsedStore.user.userId,
					userName: parsedStore.user.name,
					companyId: parsedStore.company.companyId,
					dispatch,
					logoutFunction: logout,
				});
			} else {
				socketInit({
					userId: parsedStore.user.userId,
					userName: parsedStore.user.name,
					companyId: 0,
					dispatch,
					logoutFunction: logout,
				});
			}
		}
	};
}

export function login(userName, password, selected, cleanupForm, navigate) {
	return (dispatch) => {
		dispatch(apiStart());
		Api.post("/authentication/login", {
			userName,
			password,
		})
			.then((response) => {
				if (response.data.success) {
					if (selected) {
						const credentials = { userName, password, selected };
						localStorage.setItem("credentials", JSON.stringify(credentials));
					} else {
						localStorage.removeItem("credentials");
					}
					const { token, user } = response.data.data;
					Api.defaults.headers.common.Authorization = `Bearer ${token}`;
					cleanupForm();
					dispatch(requestSuccess(LOGIN, response.data.data));
					socketInit({
						userId: user.id,
						userName: user.fullName,
						companyId: 0,
						dispatch,
						logoutFunction: logout,
					});
					dispatch(apiEnd());
					navigate("/chooseCompany");
				} else {
					dispatch(apiEnd());
					dispatch(requestFail(response.data.message));
				}
			})
			.catch((error) => {
				dispatch(apiEnd());
				dispatch(apiError(error.message));
			});
	};
}

export function chooseCompany(prevCompanyId, newCompanyId, navigate) {
	return (dispatch) => {
		dispatch(apiStart());
		Api.post("/authentication/chooseCompany", {
			prevCompanyId,
			newCompanyId,
		})
			.then((response) => {
				if (response.data.success) {
					socketClose();
					const { token, user } = response.data.data;
					Api.defaults.headers.common.Authorization = `Bearer ${token}`;
					dispatch(requestSuccess(CHOOSE_COMPANY, response.data.data));
					socketInit({
						userId: user.user.id,
						userName: user.user.fullName,
						companyId: newCompanyId,
						dispatch,
						logoutFunction: logout,
					});
					dispatch(apiEnd());
					const defaultMenu = get(user, "defaultMenu", {});
					const parentPath = get(defaultMenu, "parent.path", "");
					const menuPath = get(defaultMenu, "path", "/monitor");
					const defaultMenuPath = parentPath ? `${parentPath}${menuPath}` : menuPath;
					const targetPath = user.defaultMenuId ? defaultMenuPath : "/monitor";
					navigate(targetPath || "/monitor");
				} else {
					dispatch(apiEnd());
					dispatch(requestFail(response.data.message));
				}
			})
			.catch((error) => {
				dispatch(apiEnd());
				dispatch(apiError(error.message));
			});
	};
}

export function register(userObj, registerToken, pin, cleanupForm, navigate) {
	return (dispatch) => {
		dispatch(apiStart());
		Api.post("/authentication/register", {
			user: userObj,
			token: registerToken,
			pin,
		})
			.then((response) => {
				if (response.data.success) {
					const { token, user } = response.data.data;
					Api.defaults.headers.common.Authorization = `Bearer ${token}`;
					cleanupForm();
					dispatch(requestSuccess(LOGIN, response.data.data));
					socketInit({
						userId: user.id,
						userName: user.fullName,
						companyId: 0,
						dispatch,
						logoutFunction: logout,
					});
					dispatch(apiEnd());
					navigate("/chooseCompany");
				} else {
					dispatch(apiEnd());
					dispatch(requestFail(response.data.message));
				}
			})
			.catch((error) => {
				dispatch(apiEnd());
				dispatch(apiError(error));
			});
	};
}

export function forgotPassword(userName, cleanupForm, navigate) {
	return (dispatch) => {
		dispatch(apiStart());
		Api.post("/authentication/forgotPassword", {
			userName,
		})
			.then((response) => {
				if (response.data.success) {
					cleanupForm();
					dispatch(apiEnd());
					if (!isEmpty(response.data.data)) {
						navigate(`/auth/resetPassword/${response.data.data}`, { replace: true });
					} else {
						dispatch(displayNotification({ text: "Le hemos enviado un pin de seguridad a su correo. Por favor, verifique en su buzón." }));
						navigate("/auth/verifyPin", { replace: true });
					}
				} else {
					dispatch(apiEnd());
					dispatch(requestFail(response.data.message));
				}
			})
			.catch((error) => {
				dispatch(apiEnd());
				dispatch(apiError(error.message));
			});
	};
}

export function verifyPin(pin, cleanupForm, navigate) {
	return (dispatch) => {
		dispatch(apiStart());
		Api.post("/authentication/verifyPin", {
			pin,
		})
			.then((response) => {
				if (response.data.success) {
					cleanupForm();
					dispatch(apiEnd());
					navigate(`/auth/resetPassword/${response.data.data}`, { replace: true });
				} else {
					dispatch(apiEnd());
					dispatch(requestFail(response.data.message));
				}
			})
			.catch((error) => {
				dispatch(apiEnd());
				dispatch(apiError(error.message));
			});
	};
}

export function resetPassword(token, password, cleanupForm, navigate) {
	return (dispatch) => {
		dispatch(apiStart());
		Api.post("/authentication/resetPassword", {
			token,
			password,
		})
			.then((response) => {
				if (response.data.success) {
					dispatch(displayNotification({ text: "Contraseña actualizada. Inicie sesión", isTemporary: true }));
					cleanupForm();
					dispatch(apiEnd());
					navigate("/auth/login", { replace: true });
				} else {
					dispatch(apiEnd());
					dispatch(requestFail(response.data.message));
				}
			})
			.catch((error) => {
				dispatch(apiEnd());
				dispatch(apiError(error.message));
			});
	};
}

export function changeState(newState) {
	return (dispatch) => {
		dispatch(apiStart());
		Api.post("/authentication/changeState", {
			newState,
		})
			.then((response) => {
				if (response.data.success) {
					dispatch(requestSuccess(CHANGE_STATE, response.data.data));
					dispatch(apiEnd());
				} else {
					dispatch(apiEnd());
					dispatch(requestFail(response.data.message));
				}
			})
			.catch((error) => {
				dispatch(apiEnd());
				dispatch(apiError(error.message));
			});
	};
}
